import cn from "app/utils/classnames";
import useAnimatedScorePopup from "app/utils/useAnimatedScorePopup";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import { useCountUp } from "use-count-up";

/*
anim states:
- 0: idle
- 1: faded out
- 2: faded in
- 3: short delay
- 4: show current score
- 5: show 'x'
- 6: show multiplier
- 7: show '='
- 8: show final score
- 9: finished/idle
*/

// Used during end animation
const TRANS_CLASS_NAME = "transition-all duration-400";

export interface ScoreDisplayHandle {
  playMultLoss: (penalty: number) => void;
}

interface ScoreDisplayProps {
  startingScore: number;
  multiplier: number;
  playAnimation: boolean;
  onAnimFinished: () => void;
}

const ScoreDisplay = forwardRef<ScoreDisplayHandle, ScoreDisplayProps>(
  (
    {
      startingScore,
      multiplier,
      playAnimation,
      onAnimFinished,
    }: ScoreDisplayProps,
    ref,
  ) => {
    const [animState, setAnimState] = useState(0);

    const { value: finalScoreVal } = useCountUp({
      isCounting: playAnimation && animState >= 7,
      end: startingScore * multiplier,
      duration: 1.0,
    });

    const {
      animatePopup: animateMultLoss,
      visibleNumber: visibleMultLoss,
      showPopup: showMultLoss,
    } = useAnimatedScorePopup();

    useImperativeHandle(ref, () => ({
      // Plays the "-x.x" popup animation
      playMultLoss(penalty: number) {
        animateMultLoss(penalty);
      },
    }));

    useEffect(() => {
      if (!playAnimation || animState >= 9) {
        return;
      }

      function advanceAnimState() {
        if (animState >= 9) return;

        const newStateIndex = animState + 1;
        setAnimState(newStateIndex);

        if (newStateIndex >= 9) {
          onAnimFinished();
        }
      }

      function skipToAnimEnd() {
        if (animState >= 9) return;

        setAnimState(9);
        onAnimFinished();
      }

      const timer = setTimeout(advanceAnimState, 600);
      window.addEventListener("mousedown", skipToAnimEnd);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("mousedown", skipToAnimEnd);
      };
    }, [animState, setAnimState, onAnimFinished, playAnimation]);

    return (
      <div className={cn("flex justify-center gap-6")}>
        <div // Container for header + digits
          className={cn(
            "flex flex-col items-center",
            animState === 1 ? "opacity-0" : "opacity-100",
            TRANS_CLASS_NAME,
          )}
        >
          {/* Headers */}
          {animState <= 1 ? (
            <p className={cn("text-sm uppercase", TRANS_CLASS_NAME)}>
              Multiplier
            </p>
          ) : (
            animState >= 2 && <p className="text-sm uppercase">Final Score</p>
          )}
          <div // Container for all digits
            className={cn(
              "flex justify-center items-end gap-2",
              "font-light text-xl",
            )}
          >
            {animState < 2 ? (
              <div // Gameplay-phase multiplier digit
                className="relative"
              >
                <DigitDisplay digit={multiplier.toFixed(1)} />
                <p // MULT PENALTY POPUP
                  className={cn(
                    "absolute top-0 bott0m-0 right-0 left-11",
                    "text-2xl",
                    showMultLoss ? "opacity-100" : "opacity-0",
                    showMultLoss ? "translate-x-0" : "translate-x-10",
                    "transition-all duration-300 ease-in-out",
                    "text-red-500",
                  )}
                >
                  -{visibleMultLoss.toFixed(1)}
                </p>
              </div>
            ) : (
              // Round end animation stuff:
              <>
                <div // Prevents child DigitDisplay from flickering during animation
                  hidden={animState < 3}
                >
                  <DigitDisplay // Current score digit dislpay
                    // label="Current"
                    digit={startingScore.toLocaleString()}
                    className={cn(
                      animState >= 4 ? "opacity-100" : "opacity-0",
                      TRANS_CLASS_NAME,
                    )}
                  />
                </div>
                <div // "x" char
                  className={cn(
                    "mb-0.5",
                    animState >= 5 ? "opacity-100" : "opacity-0",
                    TRANS_CLASS_NAME,
                  )}
                >
                  x
                </div>
                <DigitDisplay
                  // label="Multiplier"
                  digit={multiplier.toFixed(1)}
                  className={cn(
                    animState >= 6 ? "opacity-100" : "opacity-0",
                    TRANS_CLASS_NAME,
                  )}
                />
                <div // "=" char
                  className={cn(
                    "mb-0.5",
                    animState >= 7 ? "opacity-100" : "opacity-0",
                    TRANS_CLASS_NAME,
                  )}
                >
                  =
                </div>
                <DigitDisplay
                  // label="Final"
                  digit={Math.ceil(finalScoreVal as number).toLocaleString()}
                  className={cn(
                    "min-w-16",
                    animState >= 8 ? "opacity-100" : "opacity-0",
                    TRANS_CLASS_NAME,
                  )}
                />
              </>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default ScoreDisplay;

interface DigitDisplayProps {
  label?: string;
  digit: number | string;
  className?: string;
}

function DigitDisplay(props: DigitDisplayProps) {
  return (
    <div className={cn("flex flex-col items-center", props.className)}>
      <div className="text-sm uppercase">{props.label}</div>
      <div className="text-2xl">{props.digit}</div>
    </div>
  );
}
